import * as React from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import type { ClientsWithUsers } from "@/client-admin";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface SelectClientCodeComboBoxProps {
  value: string | null;
  onChange: (value: string | null) => void;
  data: ClientsWithUsers | undefined;
  placeholder?: string;

  isLoading?: boolean;
}

export function SelectClientCodeComboBox({
  value,
  onChange,
  data,
  placeholder = "Select client code...",
  isLoading = false,
}: SelectClientCodeComboBoxProps) {
  const [open, setOpen] = React.useState(false);

  const clientCodes = React.useMemo(() => {
    if (!data?.data) return [];
    return data.data.map((client) => ({
      value: client.client_code,
      label: client.client_code,
    }));
  }, [data]);

  const selectedLabel = React.useMemo(() => {
    if (!value) return placeholder;
    return clientCodes.find((code) => code.value === value)?.label || placeholder;
  }, [value, clientCodes, placeholder]);

  if (isLoading)
    return (
      <Button variant="outline" disabled>
        {placeholder}
      </Button>
    );
  if (!data) return <Button variant="outline">{placeholder}</Button>;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild className="w-full sm:w-[250px]">
        <Button variant="outline" role="combobox" aria-expanded={open} className={`justify-between`}>
          {selectedLabel}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent>
        <Command>
          <CommandInput placeholder="Search client code..." />
          <CommandList>
            <CommandEmpty>No client code found.</CommandEmpty>
            <CommandGroup>
              {value && (
                <CommandItem
                  key="clear-selection"
                  value="clear-selection"
                  onSelect={() => {
                    onChange(null);
                    setOpen(false);
                  }}
                  className="text-muted-foreground"
                >
                  <X className="mr-2 h-4 w-4" />
                  Clear selection
                </CommandItem>
              )}
              {clientCodes.map((clientCode) => (
                <CommandItem
                  key={clientCode.value}
                  value={clientCode.value}
                  onSelect={(currentValue) => {
                    onChange(currentValue === value ? null : currentValue);
                    setOpen(false);
                  }}
                >
                  <Check className={cn("mr-2 h-4 w-4", value === clientCode.value ? "opacity-100" : "opacity-0")} />
                  {clientCode.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
